import "./HomeAside.scss"

import React from "react"
import { Post } from "@fider/models"
import { Icon } from "@fider/components"
import { PostCategory } from "@fider/services/khcn"
import { CategoryArcMenu } from "./CategoryArcMenu"
import { CATEGORY_ICONS } from "./categoryIcons"
import IconPlus from "@fider/assets/images/heroicons-plus.svg"
import IconThumbsUp from "@fider/assets/images/heroicons-thumbsup.svg"
import { i18n } from "@lingui/core"

// Tùy biến KHCN: cột phải trang chủ theo bản thiết kế — nút đăng ý tưởng (menu vòng cung chọn hạng mục),
// chủ đề nổi bật (đếm thật theo hạng mục), thẻ cổ động với bộ não phát sáng, và top ý tưởng được quan tâm
// (nhiều bình chọn nhất trong danh sách đã tải).

const Chevron = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 6l6 6-6 6" />
  </svg>
)

// ---------- Nút "Đăng ý tưởng" ----------
interface PostCtaCardProps {
  categories: PostCategory[]
  canPost: boolean
  disabled: boolean
  onSelect: (slug: string) => void
  onSignIn: () => void
}

export const PostCtaCard = (props: PostCtaCardProps) => (
  <div className="kh-aside__cta">
    {props.canPost ? (
      <CategoryArcMenu categories={props.categories} disabled={props.disabled} onSelect={props.onSelect}>
        <Icon sprite={IconPlus} />
        <span>{i18n._({ id: "khcn.home.composer.cta", message: "Post an idea" })}</span>
      </CategoryArcMenu>
    ) : (
      <button type="button" className="kh-aside__cta-guest" onClick={props.onSignIn}>
        <Icon sprite={IconPlus} />
        <span>{i18n._({ id: "khcn.aside.signin", message: "Sign in to post an idea" })}</span>
      </button>
    )}
  </div>
)

// ---------- Chủ đề nổi bật ----------
interface TopicsCardProps {
  categories: PostCategory[]
  countPerCategory: { [slug: string]: number }
  onSelect: (slug: string | null) => void
}

export const TopicsCard = (props: TopicsCardProps) => {
  const sorted = [...props.categories].sort((a, b) => (props.countPerCategory[b.slug] || 0) - (props.countPerCategory[a.slug] || 0))
  return (
    <section className="kh-aside__card">
      <header className="kh-aside__head">
        <h3>{i18n._({ id: "khcn.aside.topics", message: "Trending topics" })}</h3>
        <button type="button" className="kh-aside__more" onClick={() => props.onSelect(null)}>
          {i18n._({ id: "khcn.aside.seeall", message: "See all" })}
          <Chevron />
        </button>
      </header>
      <ul className="kh-topics">
        {sorted.map((category) => (
          <li key={category.slug}>
            <button type="button" className="kh-topics__item" title={category.hint} onClick={() => props.onSelect(category.slug)}>
              <span className="kh-topics__icon" style={{ background: `#${category.color}` }}>
                <Icon sprite={CATEGORY_ICONS[category.slug]} />
              </span>
              <span className="kh-topics__label">{category.label}</span>
              <span className="kh-topics__count">{props.countPerCategory[category.slug] || 0}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}

// ---------- Bóng đèn ý tưởng phát sáng (SVG, animation CSS) ----------
const RAYS = [-150, -120, -90, -60, -30, 180, 0]

const IdeaBulb = () => (
  <svg className="kh-bulb" viewBox="0 0 220 240" aria-hidden="true" focusable="false">
    <defs>
      <radialGradient id="khBulbGlow" cx="50%" cy="42%" r="50%">
        <stop offset="0" stopColor="#ffe08a" stopOpacity="0.95" />
        <stop offset="0.45" stopColor="#f7c948" stopOpacity="0.35" />
        <stop offset="1" stopColor="#f7c948" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="khBulbGlass" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#fff3c4" />
        <stop offset="0.55" stopColor="#ffd766" />
        <stop offset="1" stopColor="#f2b900" />
      </linearGradient>
      <linearGradient id="khBulbBase" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor="#5aa7ff" />
        <stop offset="1" stopColor="#1d6fe8" />
      </linearGradient>
    </defs>
    <circle className="kh-bulb__glow" cx="110" cy="96" r="90" fill="url(#khBulbGlow)" />
    <g stroke="#ffe08a" strokeWidth="4" strokeLinecap="round">
      {RAYS.map((deg, i) => {
        const a = (deg * Math.PI) / 180
        const r1 = 78
        const r2 = deg === -90 ? 100 : 94
        return (
          <line
            key={deg}
            className={`kh-bulb__ray kh-bulb__ray--${(i % 4) + 1}`}
            x1={(110 + r1 * Math.cos(a)).toFixed(1)}
            y1={(96 + r1 * Math.sin(a)).toFixed(1)}
            x2={(110 + r2 * Math.cos(a)).toFixed(1)}
            y2={(96 + r2 * Math.sin(a)).toFixed(1)}
          />
        )
      })}
    </g>
    {/* bầu đèn */}
    <path
      className="kh-bulb__glass"
      d="M110 26a56 56 0 0 0-32.8 101c6.4 4.8 10.4 12 11.9 19.8h41.8c1.5-7.8 5.5-15 11.9-19.8A56 56 0 0 0 110 26z"
      fill="url(#khBulbGlass)"
    />
    <path d="M82 66c4-14 14-24 26-28" fill="none" stroke="#ffffff" strokeWidth="6" strokeLinecap="round" opacity="0.75" />
    {/* dây tóc */}
    <path d="M96 122l5-24 9 20 9-20 5 24" fill="none" stroke="#0e4cb5" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M101 122v14M119 122v14" fill="none" stroke="#0e4cb5" strokeWidth="5" strokeLinecap="round" />
    {/* đuôi đèn */}
    <rect x="86" y="152" width="48" height="12" rx="6" fill="url(#khBulbBase)" />
    <rect x="88" y="170" width="44" height="12" rx="6" fill="url(#khBulbBase)" />
    <rect x="92" y="188" width="36" height="12" rx="6" fill="url(#khBulbBase)" />
    <path d="M98 206h24l-4 10h-16z" fill="#0e4cb5" />
    {/* lấp lánh */}
    <g fill="#ffffff">
      <path className="kh-bulb__spark kh-bulb__spark--1" d="M182 36l3 8 8 3-8 3-3 8-3-8-8-3 8-3z" />
      <path className="kh-bulb__spark kh-bulb__spark--2" d="M32 70l2.2 6 6 2.2-6 2.2-2.2 6-2.2-6-6-2.2 6-2.2z" />
      <path className="kh-bulb__spark kh-bulb__spark--3" d="M190 130l2 5.5 5.5 2-5.5 2-2 5.5-2-5.5-5.5-2 5.5-2z" />
    </g>
  </svg>
)

// ---------- Thẻ cổ động ----------
export const PromoCard = ({ onAction }: { onAction: () => void }) => (
  <section className="kh-promo">
    <div className="kh-promo__text">
      <p className="kh-promo__title">
        <span>{i18n._({ id: "khcn.promo.line1", message: "Small ideas" })}</span>
        <span>{i18n._({ id: "khcn.promo.line2", message: "make" })}</span>
        <span className="kh-promo__title--gold">{i18n._({ id: "khcn.promo.line3", message: "big changes" })}</span>
      </p>
      <button type="button" className="kh-promo__btn" onClick={onAction}>
        {i18n._({ id: "khcn.promo.cta", message: "Post an idea now" })}
        <Chevron />
      </button>
    </div>
    <IdeaBulb />
  </section>
)

// ---------- Ý tưởng được quan tâm ----------
interface TopPostsCardProps {
  posts: Post[]
  onPostClick: (postNumber: number, slug: string) => void
  onSeeAll: () => void
}

export const TopPostsCard = (props: TopPostsCardProps) => {
  const top = [...props.posts]
    .filter((post) => post.isApproved)
    .sort((a, b) => b.votesCount - a.votesCount)
    .slice(0, 5)

  if (top.length === 0) {
    return null
  }

  return (
    <section className="kh-aside__card">
      <header className="kh-aside__head">
        <h3>{i18n._({ id: "khcn.aside.top", message: "Most-wanted ideas" })}</h3>
        <button type="button" className="kh-aside__more" onClick={props.onSeeAll}>
          {i18n._({ id: "khcn.aside.seeall", message: "See all" })}
          <Chevron />
        </button>
      </header>
      <ol className="kh-top">
        {top.map((post, index) => (
          <li key={post.id}>
            <a
              href={`/posts/${post.number}/${post.slug}`}
              className="kh-top__item"
              onClick={(e) => {
                e.preventDefault()
                props.onPostClick(post.number, post.slug)
              }}
            >
              <span className={`kh-top__rank kh-top__rank--${Math.min(index + 1, 4)}`}>{index + 1}</span>
              <span className="kh-top__title">{post.title}</span>
              <span className="kh-top__votes">
                <Icon sprite={IconThumbsUp} />
                {post.votesCount}
              </span>
            </a>
          </li>
        ))}
      </ol>
    </section>
  )
}
